import { tournamentSignupToCartLine } from "../../../utility/signupCart";
import { isDueCartLine } from "../../../utility/signupPaymentQuote";

const CANCELLED_STATUSES = new Set(["cancelled", "denied"]);

export const MY_SIGNUP_TABS = [
  { id: "all", label: "All" },
  { id: "due", label: "Due" },
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "cancelled", label: "Cancelled" },
];

const TAB_ALIASES = {
  paid: "upcoming",
};

export const squadIdOf = (signup) =>
  signup?.squadId || signup?.squadID || signup?.squad?.id || null;

export const squadHasPassed = (signup, now = new Date()) => {
  const date = signup?.squad?.squadDate;
  if (!date) return false;
  const [year, month, day] = String(date).split("-").map(Number);
  if (!year || !month || !day) return false;
  return now > new Date(year, month - 1, day, 23, 59, 59, 999);
};

export const signupTab = (signup, now = new Date()) => {
  if (CANCELLED_STATUSES.has(signup?.status)) return "cancelled";
  if (isDueCartLine(tournamentSignupToCartLine(signup))) return "due";
  if (squadHasPassed(signup, now)) return "past";
  return "upcoming";
};

export const visibleMySignupTabs = (dueCount = 0) =>
  MY_SIGNUP_TABS.filter((tab) => tab.id !== "due" || dueCount > 0);

export const resolveMySignupTab = (requested, dueCount = 0) => {
  const tab = TAB_ALIASES[requested] || requested;
  const allowed = new Set(visibleMySignupTabs(dueCount).map((item) => item.id));
  if (allowed.has(tab)) return tab;
  return "all";
};

const searchHaystack = (signup) => {
  const bowlers = (signup?.signupBowlers || [])
    .map((bowler) => bowler?.name)
    .filter(Boolean)
    .join(" ");
  const center = signup?.squad?.bowlingCenters || {};
  return [
    signup?.tournament?.name,
    signup?.tournamentEvents?.eventName,
    bowlers,
    center.centerName,
    center.formattedAddress,
    signup?.squad?.squadDate,
    signup?.squad?.squadTime,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export const signupMatchesQuery = (signup, query) => {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  return searchHaystack(signup).includes(needle);
};

export const filterMySignups = (
  signups = [],
  { query = "", tournamentId = "", eventName = "", squadId = "" } = {}
) =>
  (signups || []).filter((signup) => {
    if (tournamentId && signup.tournament?.id !== tournamentId) return false;
    if (eventName && signup.tournamentEvents?.eventName !== eventName) {
      return false;
    }
    if (squadId && squadIdOf(signup) !== squadId) return false;
    if (!signupMatchesQuery(signup, query)) return false;
    return true;
  });

export const signupsForTab = (signups = [], tab, now = new Date()) => {
  if (tab === "all" || !tab) return signups || [];
  return (signups || []).filter((signup) => signupTab(signup, now) === tab);
};

const squadSortKey = (signup) =>
  `${signup?.squad?.squadDate || ""}T${signup?.squad?.squadTime || ""}`;

const tabRank = (tab) => {
  if (tab === "due") return 0;
  if (tab === "upcoming") return 1;
  if (tab === "past") return 2;
  return 3;
};

export const sortMySignups = (signups = [], tab, now = new Date()) => {
  const sorted = [...(signups || [])];
  sorted.sort((left, right) => {
    const leftTab = signupTab(left, now);
    const rightTab = signupTab(right, now);
    if (tab === "all") {
      const rank = tabRank(leftTab) - tabRank(rightTab);
      if (rank !== 0) return rank;
    }
    const descending =
      tab === "past" ||
      tab === "cancelled" ||
      leftTab === "past" ||
      leftTab === "cancelled";
    const key = squadSortKey(left).localeCompare(squadSortKey(right));
    if (key !== 0) return descending ? -key : key;
    return String(right.createdAt || "").localeCompare(
      String(left.createdAt || "")
    );
  });
  return sorted;
};

export const mySignupTabCounts = (signups = [], now = new Date()) => {
  const counts = {
    all: (signups || []).length,
    due: 0,
    upcoming: 0,
    past: 0,
    cancelled: 0,
  };
  (signups || []).forEach((signup) => {
    counts[signupTab(signup, now)] += 1;
  });
  return counts;
};

export const getUniqueTournaments = (signups = []) => {
  const tournamentMap = new Map();
  (signups || []).forEach((signup) => {
    if (signup.tournament?.id && signup.tournament?.name) {
      tournamentMap.set(signup.tournament.id, {
        id: signup.tournament.id,
        name: signup.tournament.name,
      });
    }
  });
  return Array.from(tournamentMap.values()).sort((left, right) =>
    left.name.localeCompare(right.name)
  );
};

export const getUniqueEvents = (signups = []) => {
  const names = [
    ...new Set(
      (signups || [])
        .map((signup) => signup.tournamentEvents?.eventName)
        .filter(Boolean)
    ),
  ];
  return names.sort((left, right) => left.localeCompare(right));
};

export const getUniqueSquads = (signups = [], tournamentId) => {
  const squadMap = new Map();
  (signups || []).forEach((signup) => {
    if (tournamentId && signup.tournament?.id !== tournamentId) return;
    const id = squadIdOf(signup);
    if (!id || squadMap.has(id)) return;
    squadMap.set(id, {
      id,
      squadDate: signup.squad?.squadDate || "",
      squadTime: signup.squad?.squadTime || "",
      centerName: signup.squad?.bowlingCenters?.centerName || "",
      tournamentName: signup.tournament?.name || "",
    });
  });
  return Array.from(squadMap.values()).sort((left, right) =>
    `${left.squadDate}T${left.squadTime}`.localeCompare(
      `${right.squadDate}T${right.squadTime}`
    )
  );
};
